/* eslint-disable react-hooks/exhaustive-deps */
import classNames from "classnames";
import React, { FocusEvent, useCallback, useMemo } from "react";
import { Form } from "react-bootstrap";
import Select from "react-select";

import { useAppSelector } from "../../redux/Store";
import { ITRFilingSelector } from "../../redux/slice/ITRFilingSlice";

interface SelectProps {
	name: string;
	options: Array<{ [key: string]: string }> | Array<string | number>;
	value?: any | null;
	onChange: (value: any) => void;
	onBlur: (e: FocusEvent<HTMLInputElement>) => void;
	label?: string;
	isRequired?: boolean;
	isInvalid?: boolean;
	errorMsg?: string;
	containerClasses?: any;
	size?: "lg" | "sm";
	menuMinWidth?: string;
	menuZIndex?: number;
	optionLabel?: string;
	optionValue?: string;
	placeholder?: string;
	isMulti?: boolean;
	isClearable?: boolean;
	isSearchable?: boolean;
	isDisabled?: boolean;
	isDataLoading?: boolean;
	closeMenuOnSelect?: boolean;
	isOptionArrayOfObject?: boolean;
	note?: string;
	formType?: "ITR" | "GENERAL";
	// true means options array is of object type, false means simple string options in array
}

const SelectInputField: React.FC<SelectProps> = (props: SelectProps) => {
  const {
    name,
    size,
    menuMinWidth = "auto",
    menuZIndex = 2000,
    isInvalid,
    containerClasses,
    options,
    value,
    onChange,
    optionLabel = "label",
    optionValue = "value",
    isMulti = false,
    isClearable = false,
    isSearchable = false,
    isDisabled = false,
    isDataLoading = false,
    closeMenuOnSelect = true,
    isOptionArrayOfObject = true,
    placeholder = "Please select an option",
    onBlur,
    errorMsg,
    note,
    label = "",
    isRequired = false,
    formType = "ITR"
  } = props;

  const { isFilingReadOnly } = useAppSelector(ITRFilingSelector);

  const isFieldDisabled = useMemo(() => {
    if (formType === "GENERAL") {
      if (isDisabled) {
        return isDisabled;
      }
      return false;
    }
    if (isFilingReadOnly) {
      return true;
    }
    return isDisabled;
  }, [isDisabled, isFilingReadOnly, formType]);

  const generateOptionLabel = (option: any) => {
    const names = optionLabel.split(",").map((name) => name.trim());

    if (names?.length > 1) {
      let newLabel = "";
      names.forEach((name) => {
        newLabel = `${newLabel} ${option[name]}`;
      });
      return newLabel;
    }

    return option[`${optionLabel}`];
  };

  const getCustomOptionLabel = useCallback((option: any) => {
    return generateOptionLabel(option);
  }, []);

  // TODO: menu should be render based on the form group condition

  const customStyles = {
    menuPortal: (base: any) => ({ ...base, zIndex: menuZIndex }),
    menu: (provided: any) => ({
      ...provided,
      minWidth: menuMinWidth
    })
  };

  return (
    <>
      <Form.Group className="form-group" controlId={name}>
        {label ? (
          <Form.Label className={classNames("small-label", isRequired && "required-field")}>
            {label}
          </Form.Label>
        ) : null}
        {isOptionArrayOfObject ? (
          <Select
            menuShouldScrollIntoView={false}
            menuPortalTarget={document.body}
            styles={customStyles}
            name={name}
            options={options}
            value={value && value}
            onChange={(selectedOption) => onChange(selectedOption)}
            placeholder={placeholder}
            isMulti={isMulti}
            isLoading={isDataLoading}
            isSearchable={isSearchable}
            isDisabled={isFieldDisabled}
            isClearable={isClearable}
            getOptionLabel={(option) => getCustomOptionLabel(option)}
            getOptionValue={(option) => option?.value || option[`${optionValue}`]}
            onBlur={onBlur}
            closeMenuOnSelect={closeMenuOnSelect}
            className={classNames(
              "eds-select",
              size && `eds-select-${size}`,
              isInvalid && "field-invalid",
              containerClasses
            )}
            classNamePrefix="eds-select"
          />
        ) : (
          <Select
            menuShouldScrollIntoView={false}
            menuPortalTarget={document.body}
            styles={customStyles}
            name={name}
            options={[...options.map((item) => ({ value: item, label: item }))]}
            value={value && [...value.map((item: any) => ({ value: item, label: item }))]}
            onChange={(selectedOption) => onChange(selectedOption)}
            placeholder={placeholder}
            isMulti={isMulti}
            isLoading={isDataLoading}
            isSearchable={isSearchable}
            isDisabled={isFieldDisabled}
            isClearable={isClearable}
            getOptionLabel={(option) => getCustomOptionLabel(option)}
            getOptionValue={(option) => option?.value || option[`${optionValue}`]}
            onBlur={onBlur}
            closeMenuOnSelect={closeMenuOnSelect}
            className={classNames(
              "eds-select",
              size && `eds-select-${size}`,
              isInvalid && "field-invalid",
              containerClasses
            )}
            classNamePrefix="eds-select"
          />
        )}
        {note && !isInvalid && !errorMsg && <Form.Text className="field-text">{note}</Form.Text>}
        {isInvalid && errorMsg ? <div className="field-error">{errorMsg}</div> : null}
      </Form.Group>
    </>
  );
};

export default SelectInputField;
